import {
  Flex, Radio, Image as AntdImage, Checkbox
} from 'antd';
import React, { useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { Typography } from 'tfc-components';

import { Question } from 'services/onboarding/types';

type Props = {
  method: UseFormReturn<{ [code: string]: string[] }>;
  question: Question;
};

const QuestionPage: React.FC<Props> = ({ method, question }) => {
  const formData = useWatch({ control: method.control });

  const flatAnswers = useMemo(() => Object.values(formData).flat(), [formData]);

  const isConditionMet = useMemo(
    () => question.condition && question.condition.requireAnyOption
      && !question.condition.requireAnyOption.some((rq) => flatAnswers.includes(rq)),
    [flatAnswers, question.condition]
  );

  // Lọc các option được hiển thị dựa trên condition của từng option
  const visibleOptions = useMemo(
    () => question.options.filter((option) => {
      if (!option.condition?.requireAnyOption?.length) return true;
      return option.condition.requireAnyOption.some((rq) => flatAnswers.includes(rq));
    }),
    [question.options, flatAnswers]
  );

  if (isConditionMet) {
    return null;
  }

  return (
    <div className="p-onboarding_question_item">
      <Typography.Text extendClasses="fs-16x24 color-eerie-black" fontweight="700">
        {question.titleByCondition?.find((item) => flatAnswers.includes(item.option))?.text
          || question.title}
      </Typography.Text>
      {(question.description || (question.descriptionByCondition
        && question.descriptionByCondition.some(
          (desc) => flatAnswers.includes(desc.option)
        ))) && (
        <Typography.Text
          textStyle="italic"
          extendClasses="fs-14x22 color-davys-grey u-mt-6"
          fontweight="400"
        >
          {question.descriptionByCondition?.find((
            desc
          ) => flatAnswers.includes(desc.option))?.text || question.description}
        </Typography.Text>
      )}
      <Controller
        control={method.control}
        name={question.code}
        render={({ field: { value, onChange } }) => {
          const isAnyMultipleFalseSelected = (value || []).some((v: string) => {
            const opt = question.options.find((o) => o.code === v);
            return opt?.multiple === false;
          });

          return (question.type === 2 ? (
            <Radio.Group
              className="p-onboarding_selection"
              onChange={(e) => onChange([e.target.value])}
              value={value?.[0]}
              options={visibleOptions.map((option) => ({
                value: option.code,
                label: (
                  <Flex gap={6} justify="center" vertical className="p-onboarding_selection_label">
                    <Flex gap={6} align="center">
                      {option.icon && (
                        <AntdImage
                          src={option.icon}
                          alt={option.title}
                          width={16}
                          height={16}
                          preview={false}
                        />
                      )}
                      <p>{option.title}</p>
                    </Flex>
                    {option.description && <Typography.Text extendClasses="fs-14x22 color-taupe-gray">{option.description}</Typography.Text>}
                  </Flex>
                )
              }))}
            />
          ) : (
            <Flex gap={12} wrap="wrap" className="u-mt-24">
              {visibleOptions.map((option) => (
                <Checkbox
                  key={option.code}
                  checked={value?.includes(option.code)}
                  className="p-onboarding_checkbox"
                  disabled={isAnyMultipleFalseSelected && !value?.includes(option.code)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (option.multiple === false) {
                        onChange([option.code]);
                      } else {
                        const filteredValues = (value || []).filter((v: string) => {
                          const opt = question.options.find((o) => o.code === v);
                          return opt?.multiple !== false;
                        });
                        onChange([...filteredValues, option.code]);
                      }
                    } else {
                      onChange((value || []).filter(
                        (v: string) => v !== option.code
                      ));
                    }
                  }}
                >
                  <Flex gap={6} justify="center" vertical className="p-onboarding_selection_label">
                    <Flex gap={6} align="center">
                      {option.icon && (
                        <AntdImage
                          src={option.icon}
                          alt={option.title}
                          width={16}
                          height={16}
                          preview={false}
                        />
                      )}
                      <p>{option.title}</p>
                    </Flex>
                    {option.description && <Typography.Text extendClasses="fs-14x22 color-taupe-gray">{option.description}</Typography.Text>}
                  </Flex>
                </Checkbox>
              ))}
            </Flex>
          ));
        }}
      />
    </div>
  );
};

export default QuestionPage;
